<template>
  <el-dialog :title="courseToEdit ? '编辑课程' : '添加课程'" :visible.sync="dialogVisible" width="50%" :before-close="handleClose">
    <el-form :model="courseForm" :rules="rules" ref="courseForm" label-width="100px">
      <!-- 添加课程封面上传 -->
      <el-form-item label="课程封面">
        <div class="upload-container">
          <el-upload
            class="avatar-uploader"
            action="#"
            :http-request="uploadImage"
            :show-file-list="false"
            :before-upload="beforeImageUpload">
            <img v-if="imageUrl" :src="imageUrl" class="avatar">
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
          <div class="upload-tip" v-if="imageUrl">
            <el-button size="mini" type="danger" @click="removeImage">移除封面</el-button>
          </div>
        </div>
      </el-form-item>

      <el-form-item label="课程名称" prop="course_name">
        <el-input v-model="courseForm.course_name" placeholder="请输入课程名称"></el-input>
      </el-form-item>

      <el-form-item label="教师姓名" prop="teacher_name">
        <el-input v-model="courseForm.teacher_name" disabled></el-input>
      </el-form-item>

      <el-form-item label="题库" prop="selectedCourse">
        <el-select v-model="courseForm.selectedCourse" placeholder="请选择题库" style="width: 100%" @change="handleCourseChange" value-key="course_level">
          <el-option
            v-for="repo in courseCategories"
            :key="repo.id"
            :label="repo.name"
            :value="{repo_id: repo.id, course_level: repo.name}"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="编程题库" prop="code_repo_id">
        <el-select v-model="courseForm.code_repo_id" placeholder="请选择编程题库" style="width: 100%" clearable>
          <el-option
            v-for="repo in courseCategories"
            :key="'code-' + repo.id"
            :label="repo.name"
            :value="repo.id"
          />
        </el-select>
      </el-form-item>

<!--
      <el-form-item label="所属院系" prop="organization">
        <el-input v-model="courseForm.organization" placeholder="请输入所属院系"></el-input>
      </el-form-item> -->

      <el-form-item label="学期" prop="term">
        <el-input v-model="courseForm.term" placeholder="请输入学期"></el-input>
      </el-form-item>

      <el-form-item label="课程简介" prop="profile">
        <el-input type="textarea" v-model="courseForm.profile" placeholder="请输入课程简介"></el-input>
      </el-form-item>

      <el-form-item label="教学目标" prop="target">
        <el-input type="textarea" v-model="courseForm.target" placeholder="请输入教学目标"></el-input>
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" @click="submitForm">确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { createCourse, updateCourse, uploadCourseCover, uploadCourseImage, getCourseById } from '@/api/course'
import { fetchPaging } from '@/api/repo' // 导入题库API
import { getToken } from '@/utils/auth' // 添加这一行导入getToken函数
import { getTokenInfo } from '@/utils/jwtUtils' // 导入获取用户信息的函数

export default {
  name: 'CreateCourse',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    courseToEdit: {
      type: Object,
      default: null
    },
    selectedCategoryId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      dialogVisible: false,
      imageUrl: '', // 存储图片URL
      imageFile: null, // 存储图片文件对象
      courseCategories: [], // 课程类别列表
      courseForm: {
        course_name: '',
        teacher_name: '',
        selectedCourse: null, // 选择的题库（包含repo_id和course_level）
        category_id: null, // 课程类别ID
        repo_id: null, // 题库ID
        code_repo_id: null,
        course_level: '', // 课程级别（用于匹配题库）
        term: '',
        organization: '',
        profile: '',
        target: '',
        image: '' // 添加image字段
      },
      rules: {
        course_name: [
          { required: true, message: '请输入课程名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        teacher_name: [
          { required: true, message: '请输入教师姓名', trigger: 'blur' }
        ],
        selectedCourse: [
          { required: true, message: '请选择课程类别', trigger: 'change' }
        ],
        organization: [
          { required: true, message: '请输入所属院系', trigger: 'blur' }
        ],
        term: [
          // 学期改为非必填项
        ]
      }
    }
  },
  watch: {
    visible: {
      async handler(val) {
        this.dialogVisible = val
        // 弹窗打开时加载课程类别
        if (val) {
          await this.loadCourseCategories()
        }
        // 在watch.visible.handler方法中
        if (val && this.courseToEdit) {
          const repoId = this.courseToEdit.repo_id || this.courseToEdit.repoId || null
          const codeRepoId = this.courseToEdit.code_repo_id || this.courseToEdit.codeRepoId || null
          const currentUserName = this.getCurrentTeacherUsername()
          this.courseForm = {
            course_name: this.courseToEdit.course_name || '',
            teacher_name: currentUserName,
            selectedCourse: this.findRepoSelection(repoId),
            category_id: this.courseToEdit.category_id || null,
            repo_id: repoId,
            code_repo_id: codeRepoId,
            course_level: '', // 编辑时不回填级别
            term: this.courseToEdit.term || '',
            organization: this.courseToEdit.organization || '',
            profile: this.courseToEdit.profile || '',
            target: this.courseToEdit.target || '',
            image: this.courseToEdit.image || '' // 添加image字段
          }

          // 如果没有图片URL，尝试从API获取
          if (!this.courseForm.image && this.courseToEdit.id) {
            try {
              const imageUrl = await this.getCourseImage(this.courseToEdit.id)
              if (imageUrl) {
                this.courseForm.image = imageUrl
              }
            } catch (error) {
              console.error('获取课程封面失败:', error)
            }
          }

          // 设置图片URL，使用getCourseImageUrl方法处理URL
          const processedImageUrl = this.getCourseImageUrl(this.courseForm.image)
          this.imageUrl = processedImageUrl || ''
          console.log('编辑课程封面URL处理:', {
            original: this.courseForm.image,
            processed: processedImageUrl,
            final: this.imageUrl
          })
        } else if (val) {
          // 清空表单数据 - 修改这部分逻辑
          // 先重置表单验证状态
          await this.$nextTick()
          if (this.$refs.courseForm) {
            this.$refs.courseForm.clearValidate()
          }

          const currentUserName = this.getCurrentTeacherUsername()

          this.courseForm = {
            course_name: '',
            teacher_name: currentUserName, // 默认设置为当前用户名
            selectedCourse: null,
            category_id: null,
            repo_id: null,
            code_repo_id: null,
            course_level: '',
            term: '',
            organization: '',
            profile: '',
            target: '',
            image: ''
          }
          // 清空图片
          this.imageUrl = ''
          this.imageFile = null
        }
      },
      immediate: true // 添加这一行，确保组件创建时立即执行一次
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit('update:visible', false)
      }
    }
  },
  methods: {
    getCurrentTeacherUsername() {
      try {
        const userInfo = getTokenInfo() || {}
        const roles = Array.isArray(userInfo.roles) ? userInfo.roles : []
        const candidate = (userInfo.userName || userInfo.realName || '').trim()
        if (roles.includes('admin') || candidate === '管理员' || candidate.toLowerCase() === 'admin') {
          return '悉迹ai'
        }
        return candidate
      } catch (error) {
        console.warn('获取用户信息失败:', error)
        return ''
      }
    },
    handleClose() {
      // 清除表单验证状态
      if (this.$refs.courseForm) {
        this.$refs.courseForm.clearValidate()
      }

      // 手动重置表单数据
      const currentUserName = this.getCurrentTeacherUsername()

      this.courseForm = {
        course_name: '',
        teacher_name: currentUserName, // 默认设置为当前用户名
        selectedCourse: null,
        category_id: null,
        repo_id: null,
        code_repo_id: null,
        course_level: '',
        term: '',
        organization: '',
        profile: '',
        target: '',
        image: ''
      }

      // 清空图片
      this.imageUrl = ''
      this.imageFile = null

      // 关闭对话框
      this.dialogVisible = false
      this.$emit('update:visible', false)
    },

    // 处理题库选择变化
    handleCourseChange(value) {
      if (value && typeof value === 'object') {
        this.courseForm.repo_id = value.repo_id
        this.courseForm.course_level = value.course_level
        console.log('选择的题库:', value)
        console.log('当前类别ID:', this.selectedCategoryId)
      }
    },

    findRepoSelection(repoId) {
      const id = Number(repoId)
      if (!id || Number.isNaN(id)) return null
      const repo = this.courseCategories.find(item => Number(item.id) === id)
      if (!repo) return null
      return { repo_id: repo.id, course_level: repo.name }
    },

    // 加载课程类别列表（从题库列表获取）
    loadCourseCategories() {
      return fetchPaging({ repoTitle: '' })
        .then(response => {
          if (response && response.code === 1 && response.data) {
            // 将题库数据转换为课程类别格式
            this.courseCategories = response.data.map(repo => ({
              id: repo.id,
              name: repo.title
            }))
          }
        })
        .catch(error => {
          console.error('加载题库列表失败:', error)
        })
    },

    // 图片上传前的验证
    beforeImageUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 8

      if (!isJPG) {
        this.$message.error('上传封面图片只能是 JPG 或 PNG 格式!')
      }
      if (!isLt2M) {
        this.$message.error('上传封面图片大小不能超过 2MB!')
      }
      return isJPG && isLt2M
    },

    // 自定义上传方法
    uploadImage(options) {
      this.imageFile = options.file
      // 创建一个本地预览URL
      this.imageUrl = URL.createObjectURL(options.file)
      // 将图片URL设置到表单中
      this.courseForm.image = this.imageUrl
    },

    // 移除图片
    removeImage() {
      this.imageUrl = ''
      this.imageFile = null
      this.courseForm.image = ''
    },

    // 上传图片到服务器并获取URL
    async uploadImageToServer() {
      if (!this.imageFile) return Promise.resolve(null)

      const formData = new FormData()
      formData.append('file', this.imageFile)

      try {
        // 显示上传中提示
        const loadingInstance = this.$loading({
          lock: true,
          text: '上传封面中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })

        // 获取课程ID，如果是编辑模式则使用现有ID
        const courseId = this.courseToEdit?.id

        let response
        let uploadUrl

        if (courseId) {
          // 如果是编辑模式，使用专门的课程封面上传接口
          response = await uploadCourseCover(courseId, formData)
        } else {
          // 如果是新建模式，先使用通用上传接口
          // 明确指定bucketName为course-cover，不要包含空格
          formData.append('bucketName', 'course-cover')
          response = await uploadCourseImage(formData)
        }

        loadingInstance.close() // 关闭加载提示

        // request.js的响应拦截器已经返回了response.data
        // 检查响应是否成功 (code: 1 表示成功)
        if (response && response.code === 1) {
          this.$message.success(response.msg || '上传封面成功')

          // 如果有返回图片URL，则使用返回的URL
          if (response.data) {
            let imageUrl = response.data
            // 使用环境变量中的MinIO地址
            const minioUrl = process.env.VUE_APP_MINIO_URL
            if (minioUrl) {
              imageUrl = `${minioUrl}${imageUrl}`
            }

            // 设置本地预览
            this.imageUrl = imageUrl
            // 更新表单中的image字段
            this.courseForm.image = imageUrl

            return imageUrl
          } else {
            // 如果没有返回URL，保持当前的本地预览URL
            return this.imageUrl
          }
        } else {
          // 请求失败
          console.error('上传请求失败:', response)
          this.$message.error(response?.msg || response?.message || '上传封面失败')
          return null
        }
      } catch (error) {
        console.error('上传封面异常:', error)

        // 添加更详细的错误信息
        let errorMessage = '上传封面失败'
        if (error.response) {
          // 服务器返回了错误状态码
          errorMessage += `: 服务器返回 ${error.response.status} 错误`
          if (error.response.data && error.response.data.message) {
            errorMessage += ` - ${error.response.data.message}`
          }
          console.error('错误响应数据:', error.response.data)
        } else if (error.request) {
          // 请求已发送但没有收到响应
          errorMessage += ': 服务器无响应，请检查服务器是否运行'
        } else {
          // 请求设置时出错
          errorMessage += `: ${error.message}`
        }

        this.$message.error(errorMessage)

        // 尝试使用本地预览URL作为备用
        if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
          console.warn('上传失败，使用本地预览URL作为临时替代')
          // 这里仅用于开发测试，实际生产环境不应该这样做
          return this.imageUrl
        }
        return null
      }
    },

    // 获取课程封面URL - 统一处理course-cover bucket
    getCourseImageUrl(imageUrl) {
      console.log('getCourseImageUrl 输入:', imageUrl)
      const { getCourseCoverUrl } = require('@/utils/minio')
      const processedUrl = getCourseCoverUrl(imageUrl)
      console.log('处理后的URL:', processedUrl)
      return processedUrl
    },

    // 获取课程封面
    async getCourseImage(courseId) {
      if (!courseId) return null

      try {
        const response = await getCourseById(courseId)

        if (response && response.code === 1 && response.data) {
          // 获取图片URL并使用统一的方法处理
          let imageUrl = response.data.image
          return this.getCourseImageUrl(imageUrl)
        }
      } catch (error) {
        console.error('获取课程封面失败:', error)
        return null
      }
    },

    async submitForm() {
      this.$refs.courseForm.validate(async (valid) => {
        if (valid) {
          // 显示加载中
          const loadingInstance = this.$loading({
            lock: true,
            text: '保存中...',
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)'
          })

          try {
            // 检查图片URL是否为blob格式
            if (this.courseForm.image && this.courseForm.image.startsWith('blob:')) {
              // 如果是blob格式，确保有图片文件可以上传
              if (!this.imageFile) {
                this.$message.error('图片文件丢失，请重新选择图片')
                loadingInstance.close()
                return
              }

              // 上传图片到服务器获取URL
              const imageUrl = await this.uploadImageToServer()
              if (imageUrl) {
                this.courseForm.image = imageUrl
              } else {
                // 如果上传失败，提示用户并终止提交
                this.$message.error('图片上传失败，请重试')
                loadingInstance.close()
                return
              }
            }

            // 检查图片URL是否有效，防止提交http://127.0.0.1:9000null这样的无效URL
            if (this.courseForm.image && this.courseForm.image.includes('9000null')) {
              this.courseForm.image = null // 将无效URL设为null
            }

            // 创建要提交的数据对象
            const submitData = { ...this.courseForm }

            // 设置category_id为当前选中的类别ID
            if (this.selectedCategoryId) {
              submitData.category_id = this.selectedCategoryId
            }

            // 删除不需要提交的字段
            delete submitData.selectedCourse
            submitData.code_repo_id = submitData.code_repo_id || null

            if (this.courseToEdit && this.courseToEdit.id) {
              submitData.id = this.courseToEdit.id
            }

            console.log('提交的数据:', submitData)

            // 使用正确的API函数
            const response = this.courseToEdit
              ? await updateCourse(submitData)
              : await createCourse(submitData)

            loadingInstance.close() // 关闭加载提示

            // request.js的响应拦截器已经返回了response.data
            // 检查响应数据是否表示成功
            if (response && response.success !== false) {
              this.$message({
                type: 'success',
                message: this.courseToEdit ? '课程更新成功' : '课程添加成功'
              })
              this.handleClose()
              this.$emit('course-saved')
            } else {
              // 后端返回了错误信息
              this.$message.error(response?.message || '操作失败，请重试')
            }
          } catch (error) {
            loadingInstance.close() // 确保加载提示被关闭
            console.error('保存课程失败:', error)
            this.$message.error('保存课程失败，请重试')
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.el-dialog__body {
  padding: 20px 40px;
}

.upload-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.avatar-uploader .el-upload:hover {
  border-color: #409EFF;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  line-height: 178px;
  text-align: center;
}

.avatar {
  width: 178px;
  height: 178px;
  display: block;
  object-fit: cover;
}

.upload-tip {
  margin-top: 10px;
}
</style>
